import { RootState } from '../../../../../../init-store';
import {
    isDevelopmentEnvironmentSelector,
    isRoomAgentMode,
    isSipRoomAgent,
} from '../../../../../shared/features/config/selectors';
import { selectIsCommandListOpen } from '../../../../../shared/features/room-agent/dtmf/command-list/selectors';
import {
    selectDebugMenuReport,
    selectIsDebugMenuOpen,
} from '../../../../../shared/features/room-agent/dtmf/debug-menu/selectors';
import {
    selectIsParticipantListOpen,
    selectParticipantRenderState,
} from '../../../../../shared/features/room-agent/dtmf/participant-list/selectors';
import { Size } from '../../../../../shared/helpers/layout-manager/types';
import {
    canChangePage,
    getDominantSpeaker,
    getFormattedCallNumber,
    getParticipantsBySettings,
    getScreenSharingParticipant,
    selectMeetingName,
    selectVideoGrid,
} from '../../../selectors';
import { drawAllOverlay } from './draw-all-overlay';
import { getImagesLoadVersion } from './draw-images';
import { selectActiveNotificationTypes } from './dtmf/canvas-notifications';
import { ParticipantLayoutEntry } from './types';

/**
 * Кэш overlay на offscreen canvas. Перерисовывает весь overlay только когда
 * изменилось релевантное состояние (имена/mute/dominant speaker/реакции/
 * пагинация/модалки/нотификации/размер слотов).
 */
export class OverlayManager {
    private readonly canvas: HTMLCanvasElement;

    private readonly ctx: CanvasRenderingContext2D;

    private lastHash = '';

    constructor(size: Size) {
        this.canvas = document.createElement('canvas');
        this.canvas.width = size.width;
        this.canvas.height = size.height;
        this.canvas.style.display = 'none';
        document.body.appendChild(this.canvas);
        const ctx = this.canvas.getContext('2d');
        if (!ctx) {
            throw new Error('OverlayManager: no 2d context');
        }
        this.ctx = ctx;
    }

    dispose() {
        this.canvas.remove();
    }

    getSize(): Size {
        return { width: this.canvas.width, height: this.canvas.height };
    }

    render(state: RootState, layout: ParticipantLayoutEntry[], size: Size): HTMLCanvasElement {
        const hash = computeOverlayHash(state, layout, size);
        if (hash !== this.lastHash) {
            this.ctx.clearRect(0, 0, size.width, size.height);
            drawAllOverlay(this.ctx, state, layout, size);
            this.lastHash = hash;
        }
        return this.canvas;
    }
}

/**
 * Сериализованный снимок всех overlay-релевантных полей стейта и layout'а.
 * JSON.stringify по маленькому объекту — порядка десятков микросекунд на кадр,
 * что пренебрежимо мало по сравнению с ~3–5 ms CPU-overhead'а отрисовки overlay.
 */
function computeOverlayHash(state: RootState, layout: ParticipantLayoutEntry[], size: Size): string {
    const dominantSpeaker = getDominantSpeaker(state);
    const screenSharingParticipant = getScreenSharingParticipant(state);
    const videoGrid = selectVideoGrid(state);

    const payload = {
        size: { w: size.width, h: size.height },
        dominant: dominantSpeaker?.id ?? null,
        presenter: screenSharingParticipant?.id ?? null,
        page: videoGrid.currentPage,
        view: videoGrid.activeGridView,
        canPrev: canChangePage(state, -1),
        canNext: canChangePage(state, 1),

        participants: layout.map((entry) => ({
            id: entry.participant.id,
            name: (entry.refParticipant ?? entry.participant).displayName ?? '',
            muted: (entry.refParticipant ?? entry.participant).audioTrackState?.muted ?? null,
            reaction: (entry.refParticipant ?? entry.participant).reaction?.reactionType ?? null,
            frame: entry.params.hasFrameBorder ?? false,
            icons: entry.params.statusIcons ?? [],
            mic: entry.hasMicrophonePermission,
            permExt: entry.isPermissionsExtended,
            area: { x: entry.area.x, y: entry.area.y, w: entry.area.width, h: entry.area.height },
            gridView: entry.gridView,
            main: entry.isMainSlot,
            mobile: entry.isMobile,
            count: entry.participantsCount,
        })),

        // frame-level overlay
        aloneScreen: isSipRoomAgent(state)
            ? {
                  visibleCount: getParticipantsBySettings(state).length,
                  dev: isDevelopmentEnvironmentSelector(state),
                  meetingName: selectMeetingName(state) ?? '',
                  callNumber: getFormattedCallNumber(state) ?? '',
              }
            : null,
        notifications: selectActiveNotificationTypes(state),
        commandList: selectIsCommandListOpen(state),
        participantList: selectIsParticipantListOpen(state) ? getParticipantListPayload(state) : false,
        debugMenu: selectIsDebugMenuOpen(state) ? selectDebugMenuReport(state) : false,
        roomAgent: isRoomAgentMode(state),
        imagesLoadVersion: getImagesLoadVersion(),
    };

    return JSON.stringify(payload);
}

/** Данные, которые рисует DTMF-список участников: страница, доступность листания, участники чанка. */
function getParticipantListPayload(state: RootState) {
    const { totalParticipants, participantListChunk, currentPageIdx, totalPages, canGoPrevPage, canGoNextPage } =
        selectParticipantRenderState(state);

    return {
        page: currentPageIdx,
        pages: totalPages,
        prev: canGoPrevPage,
        next: canGoNextPage,
        total: totalParticipants,
        items: participantListChunk.map((participant) => ({
            id: participant.id,
            name: participant.displayName ?? '',
            muted: participant.audio?.muted ?? null,
            reaction: participant.reaction?.reactionType ?? null,
        })),
    };
}
